import { StepLinearProgress, StepLinearProgressStep } from '@if/mui'
import { Box, Stack, Typography } from '@mui/material'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export type ApprovalLikelihood = 'low' | 'moderate' | 'good' | 'high'

// Each level maps to a fixed bar fill and color — the bar is a soft signal, not a precise score.
const LEVELS: Record<ApprovalLikelihood, { value: number; color: 'error' | 'warning' | 'info' | 'success' }> = {
  low: { value: 20, color: 'error' },
  moderate: { value: 45, color: 'warning' },
  good: { value: 70, color: 'info' },
  high: { value: 90, color: 'success' },
}

// Derived from LEVELS so the bar color always matches the level text beside it.
const STEPS: StepLinearProgressStep[] = Object.values(LEVELS).map(({ value, color }) => ({ from: value, color }))

export interface ApprovalLikelihoodBarProps {
  /** Small uppercase heading shown above the bar, e.g. "Customer approval". */
  label: ReactNode
  likelihood: ApprovalLikelihood
  /** Optional guidance shown under the bar, e.g. how to improve the likelihood. */
  hint?: ReactNode
}

/**
 * A read-only likelihood meter: a label row with the translated level (Low / Moderate / Good /
 * High) on the right, a colored progress bar underneath, and an optional hint line.
 */
const ApprovalLikelihoodBar = ({ label, likelihood, hint }: ApprovalLikelihoodBarProps) => {
  const { t } = useTranslation()
  const { value, color } = LEVELS[likelihood]

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
        <Typography variant="overline" color="primary" lineHeight={1.5} component="div">
          {label}
        </Typography>
        <Typography variant="subtitle2" color={`${color}.main`}>
          {t(`common.likelihood.${likelihood}`)}
        </Typography>
      </Stack>
      <StepLinearProgress value={value} steps={STEPS} />
      {hint != null && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  )
}

export default ApprovalLikelihoodBar
